import type { LayoutConfig } from '@/utils/dealCalculations'
import { SUPORTE_EVOLUCAO_SLIDE_LAYOUT } from '@/utils/suporteEvolucaoSlideLayout'
import { normalizeTextEncoding } from '@/utils/text-normalization'
import { LAYOUT_GRID_CATEGORY_COORDS } from '../../../server/src/data/layoutGridProposalLayout'
import {
  MARKETING_EVOLVE_CATEGORY_COORDS,
  MARKETING_EVOLVE_PROPOSAL_LAYOUT,
} from '../../../server/src/data/planMarketingEvolve'
import {
  MARKETING_START_CATEGORY_COORDS,
  MARKETING_START_PROPOSAL_LAYOUT,
} from '../../../server/src/data/planMarketingStart'
import { SITE_INSTITUCIONAL_CATEGORY_COORDS } from '../../../server/src/data/planSiteInstitucional'
import {
  ROCKET_GALAXY_CATEGORY_COORDS,
  ROCKET_GALAXY_PROPOSAL_LAYOUT,
} from '../../../server/src/data/planRocketSearchGalaxy'

/** Galaxy: fidelidade abaixo do card de preço. Fonte em planRocketSearchGalaxy. */

export type CategoryCoordsMap = NonNullable<LayoutConfig['categoryCoords']>

/** Chave normalizada do nome do plano para lookup de layout padrão. */
export function planLayoutLookupKey(planName: string): string {
  return normalizeTextEncoding(planName).toLowerCase().trim()
}

/**
 * Posições padrão do slide de investimento — Plano START / Marketing Start.
 * Fonte: template validado no Proposal Studio (blocos 13–16).
 */
export const PLANO_START_CATEGORY_COORDS: CategoryCoordsMap = {
  ...MARKETING_START_CATEGORY_COORDS,
}

const MARKETING_NEGOCIO_LOCAL_CATEGORY_COORDS: CategoryCoordsMap = {
  'Tráfego e Google Meu Negócio': { x: 38, y: 13.9 },
  'Criação para Anúncios': { x: 68, y: 13.9 },
  'Acompanhamento Estratégico': { x: 68, y: 46.3 },
  'Presença em Redes Sociais': { x: 38, y: 46.3 },
}

const BUILTIN_PLAN_LAYOUTS: Record<string, CategoryCoordsMap> = {
  'plano start': PLANO_START_CATEGORY_COORDS,
  'marketing start': { ...MARKETING_START_CATEGORY_COORDS },
  'marketing negócio local': MARKETING_NEGOCIO_LOCAL_CATEGORY_COORDS,
  'marketing negocio local': MARKETING_NEGOCIO_LOCAL_CATEGORY_COORDS,
  'negócio local': MARKETING_NEGOCIO_LOCAL_CATEGORY_COORDS,
  'negocio local': MARKETING_NEGOCIO_LOCAL_CATEGORY_COORDS,
  'marketing evolve': { ...MARKETING_EVOLVE_CATEGORY_COORDS },
  evolve: { ...MARKETING_EVOLVE_CATEGORY_COORDS },
  start: PLANO_START_CATEGORY_COORDS,
  'desenvolvimento layout personalizado': LAYOUT_GRID_CATEGORY_COORDS,
  'layout personalizado': LAYOUT_GRID_CATEGORY_COORDS,
  'desenvolvimento e-commerce personalizado': LAYOUT_GRID_CATEGORY_COORDS,
  'e-commerce personalizado': LAYOUT_GRID_CATEGORY_COORDS,
  'implantação ecommerce padrão': {
    'O que é este plano': { x: 38, y: 10 },
    'Ajustes Inclusos': { x: 38, y: 38 },
    'Estrutura do template (já funcional)': { x: 68, y: 10 },
    'Vs. Layout Personalizado': { x: 68, y: 52 },
  },
  'implantacao ecommerce padrao': {
    'O que é este plano': { x: 38, y: 10 },
    'Ajustes Inclusos': { x: 38, y: 38 },
    'Estrutura do template (já funcional)': { x: 68, y: 10 },
    'Vs. Layout Personalizado': { x: 68, y: 52 },
  },
  'desenvolvimento site institucional': SITE_INSTITUCIONAL_CATEGORY_COORDS,
  'site institucional': SITE_INSTITUCIONAL_CATEGORY_COORDS,
  'suporte e evolução': SUPORTE_EVOLUCAO_SLIDE_LAYOUT.categoryCoords as CategoryCoordsMap,
  seo: {
    'Conteúdo SEO': { x: 38, y: 10 },
    'Análise e Execução Técnica': { x: 68, y: 10 },
  },
  'rocket search - galaxy': { ...ROCKET_GALAXY_CATEGORY_COORDS },
  galaxy: { ...ROCKET_GALAXY_CATEGORY_COORDS },
}

const BUILTIN_PLAN_SLIDE_LAYOUTS: Record<string, Partial<LayoutConfig>> = {
  'marketing start': { ...MARKETING_START_PROPOSAL_LAYOUT },
  'marketing evolve': { ...MARKETING_EVOLVE_PROPOSAL_LAYOUT },
  evolve: { ...MARKETING_EVOLVE_PROPOSAL_LAYOUT },
  'suporte e evolução': SUPORTE_EVOLUCAO_SLIDE_LAYOUT,
  'rocket search - galaxy': { ...ROCKET_GALAXY_PROPOSAL_LAYOUT },
  galaxy: { ...ROCKET_GALAXY_PROPOSAL_LAYOUT },
}

export function getBuiltinPlanCategoryCoords(planName: string): CategoryCoordsMap | undefined {
  const key = planLayoutLookupKey(planName)
  return BUILTIN_PLAN_LAYOUTS[key]
}

export function getBuiltinPlanSlideLayout(planName: string): Partial<LayoutConfig> | undefined {
  const key = planLayoutLookupKey(planName)
  return BUILTIN_PLAN_SLIDE_LAYOUTS[key]
}

/** Galaxy usa o template travado, mesmo se o plano no banco ainda tiver o layout antigo. */
export function lockedRocketGalaxyLayout(planName: string): Partial<LayoutConfig> | undefined {
  const key = planLayoutLookupKey(planName)
  if (key !== 'rocket search - galaxy' && key !== 'galaxy') return undefined
  return getBuiltinPlanSlideLayout(planName)
}
